import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import courseService from '../../../../services/CourseService';
import {
  ActivityQuestionScore,
  ModifyActivityQuestionScoresResponse,
} from '@beable/roster-backend-client-v1';
import { ModifyDialogProps, Section } from './UserLessonProgressTable';
import Alert from '@mui/material/Alert';

interface ModifyScoreDialogProps {
  open: boolean;
  props?: ModifyDialogProps;
  handleClose: () => void;
  setNewActivityData: (data: ModifyActivityQuestionScoresResponse, lessonId: string, section: Section) => void;
}
export default function ModifyScoreDialog({
  open,
  props,
  handleClose,
  setNewActivityData
}: ModifyScoreDialogProps) {
  const [activityQuestions, setActivityQuestions] =useState<ActivityQuestionScore[]>();
  const [correctQuestions, setCorrectQuestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [disabled, setDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updateResult, setUpdateResult] = useState<undefined | string>(undefined);
  function getCorrectQuestionNumbers(
    activityQuestions: ActivityQuestionScore[],
  ) {
    const correct = activityQuestions
      .filter((aq) => aq.score === 1)
      ?.map((q) => q.questionNumber);
    return correct || [];
  }

  const onClose = () => {
    setUpdateResult(undefined);
    handleClose()
  }
  useEffect(() => {
    const fetchActivityQuestionScores = async () => {
      if (open && props && props.activityAttemptId) {
        setIsLoading(true);
        const results = await courseService.getActivityQuestionScores(
          props.activityAttemptId,
        );
        setActivityQuestions(results);
        if (results) {
          const correct = getCorrectQuestionNumbers(results);
          setCorrectQuestions(correct);
        }
        setIsLoading(false);
      }
    };
    fetchActivityQuestionScores();
  }, [open, props]);

  const handleToggle = (activityQuestion: ActivityQuestionScore) => () => {
    const currentIndex = correctQuestions?.indexOf(
      activityQuestion.questionNumber!,
    );
    const newCorrect = correctQuestions ? [...correctQuestions] : [];

    if (currentIndex === -1) {
      newCorrect.push(activityQuestion.questionNumber!);
    } else {
      newCorrect.splice(currentIndex, 1);
    }

    setCorrectQuestions(newCorrect);
    setUpdateResult(undefined);
    setDisabled(false);
  };

  const handleModifyScores = async () => {
    setDisabled(true);
    setSaving(true);
    const changedScores = activityQuestions?.filter((aq) => {
      return (
        (aq.score && !correctQuestions.includes(aq.questionNumber!)) ||
        (!aq.score && correctQuestions.includes(aq.questionNumber!))
      );
    });
    const newScores = changedScores?.map((s) => {
      return {
        activityQuestionId: s.activityQuestionId,
        score: +!s.score,
      };
    });
    if (newScores && props ) {
      const result = await courseService.modifyActivityQuestionScores(
        props.activityAttemptId,
        newScores,
        props.courseType,
      );
      if(result) {
        setUpdateResult('success');
        setNewActivityData(result, props.lessonId, props.section);
      } else {
        setUpdateResult('error');
      }
    }    
    setSaving(false);
    
  };

  if (!open || isLoading || !props) {
    return <></>;
  }

  return (
    <Dialog
      aria-labelledby='modify-score-modal-title'
      aria-describedby='modify-score-description'
      open={open}
      scroll='body'
      onClose={onClose}
    >
      <DialogContent>
        {(!activityQuestions || !activityQuestions.length) && (
          <Typography>
            Unable to find activity questions. Please try again later.
          </Typography>
        )}
        {activityQuestions && activityQuestions.length && (
          <>
            <Typography variant='h5'>{props?.lessonName}</Typography>
            <List
              subheader={<ListSubheader>Modify Question Scores</ListSubheader>}
            >
              {activityQuestions.map((activityQuestion) => (
                <ListItem key={activityQuestion.activityQuestionId}>
                  <ListItemText>{`Question ${activityQuestion.questionNumber}`}</ListItemText>
                  <Switch
                    edge='end'
                    color='primary'
                    onChange={handleToggle(activityQuestion)}
                    checked={
                      correctQuestions.find(
                        (q) => q === activityQuestion.questionNumber!,
                      ) > 0
                    }
                    disabled={saving}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          color='primary'
          onClick={handleModifyScores}
          disabled={disabled}
        >
          {saving ? <CircularProgress color='primary' size='1rem' /> : 'Update'}
        </Button>
        <Button
          variant='contained'
          color='secondary'
          onClick={onClose}
          disabled={saving}
        >
          Close
        </Button>
      </DialogActions>
      {updateResult && (
        <Alert severity={updateResult === 'error' ? 'error' : 'success'}>
          {updateResult === 'error'
            ? 'Error modifying scores. Please Try Again!'
            : 'Scores updated successfully!'}
        </Alert>
      )}
    </Dialog>
  );
}